import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Spinner,
  Alert,
  InputGroup
} from 'react-bootstrap';
import { createStream } from '../../../store/actions/StreamActions';
import swal from 'sweetalert';

const CreateStream = ({ creating, error, createStream }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    serverUrl: ''
  });
  const [validated, setValidated] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);

    // Get server URL from environment or use default (hostname only, no port)
    const serverUrl = formData.serverUrl || window.location.hostname;
    
    createStream({
      name: formData.name,
      description: formData.description,
      serverUrl: serverUrl
    }, (err, result) => {
      if (!err && result) {
        swal({
          title: "Stream Created!",
          text: "Your stream has been created successfully.",
          icon: "success",
          button: "View Stream Details",
        }).then(() => {
          navigate(`/streams/${result.id}`);
        });
      } else {
        swal("Error!", err?.message || "Failed to create stream", "error");
      }
    });
  };

  return (
    <>
      <div className="form-head d-flex mb-3 mb-md-4 align-items-start">
        <div className="me-auto d-none d-lg-block">
          <h3 className="text-black font-w600">Create Stream</h3>
          <p className="mb-0">Set up a new RTMP stream for broadcasting</p>
        </div>
      </div>

      <Row>
        <Col lg={8}>
          <Card>
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible>
                  {error}
                </Alert>
              )}

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Form.Group className="mb-4">
                  <Form.Label>Stream Name *</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter stream name (e.g., My Live Stream)"
                    disabled={creating}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a stream name.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter stream description (optional)"
                    disabled={creating}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>Stream Server URL</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      name="serverUrl"
                      value={formData.serverUrl}
                      onChange={handleChange}
                      placeholder={window.location.hostname}
                      disabled={creating}
                    />
                    <InputGroup.Text>
                      <i className="fas fa-server"></i>
                    </InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Leave empty to use default server. Format: hostname (port is hidden)
                  </Form.Text>
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate('/streams')}
                    disabled={creating}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={creating}
                  >
                    {creating ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Creating Stream...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus me-2"></i>
                        Create Stream
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="bg-light">
            <Card.Body>
              <h5 className="mb-3">
                <i className="fas fa-info-circle me-2"></i>
                Stream Information
              </h5>
              <p className="text-muted mb-3">
                After creating a stream, you will receive:
              </p>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  <strong>RTMP URL</strong> for OBS/Streaming software
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  <strong>Stream Key</strong> unique identifier
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  <strong>HLS URL</strong> for video players
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  <strong>Real-time status</strong> monitoring
                </li>
              </ul>
            </Card.Body>
          </Card>

          <Card className="mt-3">
            <Card.Body>
              <h6 className="mb-3">OBS Studio Settings</h6>
              <div className="bg-dark text-light p-3 rounded">
                <small>
                  <strong>Service:</strong> Custom<br/>
                  <strong>Server:</strong> rtmp://your-server/live<br/>
                  <strong>Stream Key:</strong> (auto-generated)
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = (state) => ({
  creating: state.streams.creating,
  error: state.streams.error
});

const mapDispatchToProps = {
  createStream
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateStream);
